import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useStytchB2BClient } from '@stytch/react/b2b';
import { useSearchParams } from 'react-router';
import { z } from 'zod';

import { businessEmail, useAppForm } from '@/components/Form';
import { FieldLabel } from '@/components/Form/fields';
import { Text } from '@/components/typography';
import { maxEmailLength } from '@/constants/numbers';
import { getStytchAPIErrorMessage } from '@/error/StytchError';
import { useNotification } from '@/providers/Notification';
import { ROUTES } from '@/router';

import { getDiscoveryRedirectUrl } from './discoveryRedirect';
import RateLimit from './RateLimitButton';

type EmailSignInFormProps = {
  organizationId?: string;
  onStartAuth?: () => void;
};

const EmailSignInForm = ({ organizationId, onStartAuth }: EmailSignInFormProps) => {
  const stytch = useStytchB2BClient();
  const [searchParams] = useSearchParams();
  const { pushNotification, removeAllNotifications } = useNotification();
  const formSchema = z.object({
    email: businessEmail(),
  });

  const form = useAppForm({
    defaultValues: {
      email: '',
    },
    validators: {
      onSubmit: formSchema,
    },
    onSubmit: async ({ value: { email } }) => {
      const redirectUrl = organizationId
        ? `${window.location.origin}${ROUTES.signIn.path}`
        : getDiscoveryRedirectUrl(false, searchParams.get('redirect'));

      onStartAuth?.();

      try {
        if (organizationId) {
          // Tenant-specific magic link (direct to organization)
          await stytch.magicLinks.email.loginOrSignup({
            email_address: email,
            organization_id: organizationId,
            login_redirect_url: redirectUrl,
          });
        } else {
          // Discovery magic link (find organizations for email)
          await stytch.magicLinks.email.discovery.send({
            email_address: email,
            discovery_redirect_url: redirectUrl,
          });
        }

        removeAllNotifications();
        pushNotification({
          type: 'success',
          message: (
            <Trans>
              A one-time access link was sent to{' '}
              <Text variant="caption1" weight="extra-bold">
                {email}
              </Text>
              . If you don't receive an email, reach out to support@channel99.com or try again.
            </Trans>
          ),
        });
      } catch (e) {
        pushNotification({
          type: 'error',
          message: getStytchAPIErrorMessage(e, ROUTES.signInEmail.path),
        });
      }
    },
  });

  return (
    <form
      className="flex flex-col gap-7"
      onSubmit={(event) => {
        event.preventDefault();
        form.handleSubmit();
      }}
    >
      <form.AppField name="email">
        {(field) => (
          <field.Field>
            <FieldLabel htmlFor="email-sign-in-email">
              <Trans>Business email</Trans>
            </FieldLabel>
            <field.TextInput
              id="email-sign-in-email"
              inputMode="email"
              placeholder={t`name@work-email.com`}
              autoComplete="email"
              spellCheck={false}
              autoCapitalize="none"
              autoFocus
              maxLength={maxEmailLength}
              size="large"
            />
            <field.Error />
          </field.Field>
        )}
      </form.AppField>

      <form.AppForm>
        <RateLimit>
          <form.SubmitButton variant="primary" size="large" block>
            <Trans>Email me a link</Trans>
          </form.SubmitButton>
        </RateLimit>
      </form.AppForm>
    </form>
  );
};

export default EmailSignInForm;
